"use client"

import { useEffect, useState } from "react"
import { useSession } from "next-auth/react"
import { usePathname } from "next/navigation"
import Link from "next/link"
import { X, Zap, Star, Users } from "lucide-react"

const SESSION_KEY = "bcs_page_views"
const DISMISSED_KEY = "bcs_nudge_dismissed"
const TRIGGER_AT = 2 // show after this many page views

export default function SignupNudge() {
  const { data: session, status } = useSession()
  const pathname = usePathname()
  const [show, setShow] = useState(false)

  // Count page views on every navigation (regardless of session state)
  useEffect(() => {
    // Don't count on auth pages
    if (pathname.startsWith("/login") || pathname.startsWith("/register")) return

    const current = parseInt(sessionStorage.getItem(SESSION_KEY) ?? "0", 10)
    const next = current + 1
    sessionStorage.setItem(SESSION_KEY, String(next))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // Once session is resolved, check whether to show the nudge
  useEffect(() => {
    if (status === "loading") return
    // Already logged in — hide if somehow visible
    if (session) { setShow(false); return }
    // Already dismissed this session
    if (sessionStorage.getItem(DISMISSED_KEY)) return
    // Don't show on auth pages
    if (pathname.startsWith("/login") || pathname.startsWith("/register")) return

    const views = parseInt(sessionStorage.getItem(SESSION_KEY) ?? "0", 10)
    if (views >= TRIGGER_AT) {
      const t = setTimeout(() => setShow(true), 1200)
      return () => clearTimeout(t)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, status])

  function dismiss() {
    setShow(false)
    sessionStorage.setItem(DISMISSED_KEY, "1")
  }

  if (!show) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={dismiss}
      />

      {/* Modal */}
      <div className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md px-4 animate-in fade-in zoom-in-95 duration-200">
        <div className="relative bg-[#1e293b] border border-[#334155] rounded-2xl shadow-2xl overflow-hidden">

          {/* Top accent bar */}
          <div className="h-1 w-full bg-gradient-to-r from-sky-400 via-blue-500 to-sky-400" />

          {/* Close button */}
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          <div className="p-7">
            {/* Header */}
            <div className="mb-5">
              <p className="text-xs font-semibold tracking-widest text-sky-400 uppercase mb-2">
                Blue Collar Social
              </p>
              <h2 className="text-2xl font-bold text-white leading-tight">
                Join the network for<br />skilled trades
              </h2>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">
                To unlock the full features of this website, create a free account — it only takes a minute.
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-3 mb-6">
              {[
                { icon: Users, text: "Connect with verified local tradespeople" },
                { icon: Star,  text: "Read and leave verified project reviews" },
                { icon: Zap,   text: "Post jobs and get quotes — it's free" },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-sky-500/15 flex items-center justify-center">
                    <Icon size={14} className="text-sky-400" />
                  </div>
                  <span className="text-slate-300 text-sm">{text}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col gap-3">
              <Link
                href="/register"
                onClick={dismiss}
                className="block w-full text-center bg-sky-500 hover:bg-sky-400 transition-colors text-white font-semibold text-sm py-3 rounded-lg"
              >
                Create Free Account
              </Link>
              <div className="flex items-center justify-center gap-1 text-sm text-slate-500">
                Already have an account?{" "}
                <Link
                  href="/login"
                  onClick={dismiss}
                  className="text-sky-400 hover:text-sky-300 transition-colors font-medium"
                >
                  Sign in
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
